import React from 'react';
import { Sparkles, Crown, Award, Star, Hotel, Plane, Check, ArrowRight } from 'lucide-react';
import { Money } from '../contexts/CurrencyContext';

const TIER_STYLE = {
  brown: {
    icon: Award,
    border: 'border-amber-700/40',
    bg: 'bg-gradient-to-br from-amber-50 via-orange-50 to-white',
    chip: 'bg-amber-700/15 text-amber-800',
    price: 'text-amber-800',
    ring: 'ring-amber-700/30',
  },
  silver: {
    icon: Sparkles,
    border: 'border-slate-400',
    bg: 'bg-gradient-to-br from-blue-50 via-slate-50 to-white',
    chip: 'bg-blue-600/15 text-blue-700',
    price: 'text-blue-700',
    ring: 'ring-blue-500/40',
  },
  gold: {
    icon: Crown,
    border: 'border-yellow-500/50',
    bg: 'bg-gradient-to-br from-yellow-50 via-amber-50 to-white',
    chip: 'bg-yellow-500/15 text-yellow-700',
    price: 'text-yellow-700',
    ring: 'ring-yellow-500/30',
  },
  gold_plus: {
    icon: Crown,
    border: 'border-emerald-600',
    bg: 'bg-gradient-to-br from-emerald-900 via-emerald-800 to-slate-900',
    chip: 'bg-amber-400/30 text-amber-200',
    price: 'text-amber-300',
    ring: 'ring-amber-400/50',
    isDark: true,
  },
};

const StarRow = ({ count = 3 }) => (
  <div className="flex">
    {Array.from({ length: count }).map((_, i) => (
      <Star key={i} className="h-3 w-3 fill-amber-400 text-amber-400" />
    ))}
  </div>
);

const fmtDate = (s) => {
  if (!s) return '';
  try {
    return new Date(s).toLocaleDateString(undefined, { day: '2-digit', month: 'short' });
  } catch {
    return s;
  }
};

export const PackageCards = ({ data, onSelect }) => {
  if (!data || !Array.isArray(data.packages) || data.packages.length === 0) return null;
  const { packages, recommended_tier, departure_date, return_date, passengers, rooms } = data;
  return (
    <div className="space-y-2.5" data-testid="ai-package-cards">
      <div className="text-[11px] text-slate-500 px-1">
        {departure_date && return_date ? (
          <>
            <b>{fmtDate(departure_date)} → {fmtDate(return_date)}</b> · {passengers} pax · {rooms} room{rooms > 1 ? 's' : ''}
          </>
        ) : (
          <>3 packages based on your preferences</>
        )}
      </div>
      {packages.map((p) => {
        const s = TIER_STYLE[p.tier] || TIER_STYLE.silver;
        const Icon = s.icon;
        const isRec = p.tier === recommended_tier;
        const isDark = !!s.isDark;
        const subText = isDark ? 'text-amber-200/85' : 'text-slate-500';
        const titleText = isDark ? 'text-white' : 'text-slate-800';
        const bodyText = isDark ? 'text-slate-100' : 'text-slate-700';
        const subtleText = isDark ? 'text-slate-300' : 'text-slate-500';
        const isPremium = p.tier === 'gold_plus';
        return (
          <div
            key={p.tier}
            data-testid={`ai-package-${p.tier}`}
            className={`relative rounded-xl border-2 ${s.border} ${s.bg} p-3 ${isRec ? `ring-4 ${s.ring}` : ''}`}
          >
            {isPremium && (
              <div className="absolute -top-2 left-3 bg-amber-400 text-emerald-900 text-[10px] font-bold tracking-wider px-2 py-0.5 rounded-full shadow">
                ✦ PREMIUM ✦
              </div>
            )}
            {isRec && !isPremium && (
              <div className="absolute -top-2 right-3 bg-emerald-500 text-white text-[10px] font-bold tracking-wider px-2 py-0.5 rounded-full">
                BEST VALUE
              </div>
            )}
            <div className="flex items-start gap-2.5">
              <div className={`p-1.5 rounded-lg ${s.chip} flex-shrink-0`}>
                <Icon className="h-4 w-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className={`font-bold text-sm ${titleText}`}>{p.label}</span>
                  <StarRow count={p.stars} />
                  <span className={`text-[10px] uppercase tracking-wider font-semibold ${subText}`}>
                    {p.sub_label}
                  </span>
                </div>

                {isPremium && (
                  <div className="mt-1.5 flex flex-wrap gap-1">
                    <span className="text-[10px] bg-amber-400/20 text-amber-200 border border-amber-400/40 rounded-full px-2 py-0.5">
                      100 yards of Haram
                    </span>
                    <span className="text-[10px] bg-emerald-400/20 text-emerald-200 border border-emerald-400/40 rounded-full px-2 py-0.5">
                      ✈ Direct flights
                    </span>
                  </div>
                )}

                <div className={`mt-2 space-y-1 text-xs ${bodyText}`}>
                  {p.makkah_hotel ? (
                    <div className="flex gap-1.5 items-start">
                      <Hotel className={`h-3.5 w-3.5 ${isDark ? 'text-amber-300' : 'text-slate-400'} mt-0.5 flex-shrink-0`} />
                      <span className="line-clamp-1">
                        <b>Makkah:</b> {p.makkah_hotel.name}
                        {p.makkah_hotel.distance_from_haram && (
                          <span className={subtleText}> · {p.makkah_hotel.distance_from_haram}</span>
                        )}
                      </span>
                    </div>
                  ) : (
                    <div className={`italic ${subtleText}`}>Makkah hotel — none within 100 yards on these dates</div>
                  )}
                  {p.madinah_hotel ? (
                    <div className="flex gap-1.5 items-start">
                      <Hotel className={`h-3.5 w-3.5 ${isDark ? 'text-amber-300' : 'text-slate-400'} mt-0.5 flex-shrink-0`} />
                      <span className="line-clamp-1">
                        <b>Madinah:</b> {p.madinah_hotel.name}
                        {p.madinah_hotel.distance_from_haram && (
                          <span className={subtleText}> · {p.madinah_hotel.distance_from_haram}</span>
                        )}
                      </span>
                    </div>
                  ) : (
                    <div className={`italic ${subtleText}`}>Madinah hotel — none within 100 yards on these dates</div>
                  )}
                  <div className="flex gap-1.5 items-start">
                    <Plane className={`h-3.5 w-3.5 ${isDark ? 'text-amber-300' : 'text-slate-400'} mt-0.5 flex-shrink-0`} />
                    <span>
                      {isPremium && p.flight?.no_direct_available
                        ? <span className="italic">Direct flights not available on these dates — call us on 01908 220000 / 01582 808050</span>
                        : <>Flights from <Money gbp={p.flight_price_per_person_gbp} /> pp{isPremium && p.flight?.is_direct && ' · direct'}</>}
                    </span>
                  </div>
                </div>

                <div className={`mt-2.5 flex items-end justify-between gap-2 pt-2 border-t ${isDark ? 'border-white/15' : 'border-slate-200/70'}`}>
                  <div>
                    <div className={`text-[10px] uppercase tracking-wider ${subtleText}`}>Total</div>
                    <div className={`text-lg font-bold ${s.price} leading-none`}>
                      <Money gbp={p.total_price_gbp} />
                    </div>
                    <div className={`text-[10px] mt-0.5 ${subtleText}`}>
                      {(p.makkah_nights || 0) + (p.madinah_nights || 0)} nights · {p.passengers} pax
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => onSelect && onSelect(p)}
                    data-testid={`ai-package-select-${p.tier}`}
                    className={`flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full transition ${
                      isPremium
                        ? 'bg-amber-400 hover:bg-amber-300 text-emerald-900'
                        : isRec
                          ? 'bg-emerald-500 hover:bg-emerald-600 text-white'
                          : 'bg-slate-900 hover:bg-slate-800 text-white'
                    }`}
                  >
                    {isRec ? <Check className="h-3.5 w-3.5" /> : <>Select <ArrowRight className="h-3.5 w-3.5" /></>}
                    {isRec ? 'Top pick' : ''}
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default PackageCards;
